// What the reply box's submit button does, and what it says.
//
// Pure, and separate from ReplyComposer.jsx, for this repository's standing reason:
// there is no DOM harness, so a decision left inside a component is a decision nothing
// can check.
//
// TWO REQUESTS, CHOSEN BY THE BODY. Text typed is a message: POST .../messages with the
// optional status riding along, exactly as before. A blank body with a status chosen is
// a status change on its own: PATCH /api/tickets/:publicRef with `{ status }`, the same
// request the Edit panel sends — so it lands in the history as a status change, reaches
// Zendesk through the outbox, and clears a named status that no longer rolls up, all in
// `applyChanges` (src/services/tickets.js). No message row is written. A blank body with
// no status chosen sends nothing.
//
// "Blank" is after trimming, because the body is trimmed before it is sent: a box holding
// only spaces would otherwise post an empty message.

import { STATUS_LABELS } from './format.js';

/**
 * The status the request should carry, or null. The ticket's own current status picked
 * in the box is not a change, so it counts as "Leave unchanged".
 *
 * @param {{status?: string, currentStatus?: string}} input
 * @returns {string | null}
 */
export function statusToSend({ status, currentStatus }) {
  if (!status || status === currentStatus) return null;
  return status;
}

/**
 * Which request the submit sends.
 *
 * @param {{body?: string, status?: string, currentStatus?: string}} input  `status` is ''
 *   for "Leave unchanged"; `currentStatus` is the ticket's status now, when known.
 * @returns {'message' | 'status' | null}
 */
export function replyAction({ body, status, currentStatus }) {
  if (String(body ?? '').trim()) return 'message';
  if (statusToSend({ status, currentStatus })) return 'status';
  return null;
}

/**
 * The submit button's wording. A status-only change names the status it will set,
 * because the button would otherwise read "Save internal note" over a press that saves
 * no note.
 *
 * @param {{action: string|null, status?: string, messageLabel: string, submitting?: boolean}} input
 */
export function replySubmitLabel({ action, status, messageLabel, submitting = false }) {
  if (submitting) return 'Saving…';
  if (action === 'status') return `Set status to ${STATUS_LABELS[status] ?? status}`;
  return messageLabel;
}

/**
 * Whether this press opens the collaborator's "Email this to …?" step rather than
 * sending. Only a customer-facing MESSAGE emails anybody, so a status-only change is
 * never asked about, whichever mode the box is in.
 *
 * @param {{action: string|null, isInternal: boolean, confirmBeforeSending: boolean, confirming: boolean}} input
 */
export function asksToConfirm({ action, isInternal, confirmBeforeSending, confirming }) {
  return action === 'message' && !isInternal && confirmBeforeSending && !confirming;
}
